import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Prop,
  Watch,
  h,
} from "@stencil/core";

// Generic type in case we want to add properties to this event later.
type Escape = object;

@Component({
  tag: "zapier-internal-modal",
  styleUrl: "zapier-internal-modal.css",
  shadow: true,
})
export class ZapierInternalModal {
  @Element() elem!: HTMLZapierInternalModalElement;

  /** Is the modal dialog open? */
  @Prop() isOpen = false;

  /** Allow the "Escape" key to close the modal */
  @Event() escape!: EventEmitter<Escape>;

  /**
   * "large" modals take the full height of the page, and up to fixed width,
   * minus some padding. This is important for embedding scrolling containers
   * for large content, like iframes.
   */
  @Prop() size: "auto" | "large" = "auto";

  private dialog?: HTMLDialogElement;
  private oldBodyOverflow = "";

  // Show/hide modal when the `isOpen` prop changes, and enable/disable
  // scrolling the body element for less confusion when using the modal on large
  // pages
  @Watch("isOpen")
  openOrCloseModal() {
    if (!this.dialog) {
      return;
    }
    if (this.isOpen) {
      this.dialog.showModal();
      // Disable scrolling the body while this modal is open
      this.oldBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else {
      this.dialog.close();
      // Re-enable body scrolling
      document.body.style.overflow = this.oldBodyOverflow;
    }
  }

  render() {
    return (
      <Host data-theme="dark" data-size={this.size}>
        <dialog ref={this.setDialog} onCancel={this.onCancel}>
          <slot />
        </dialog>
      </Host>
    );
  }

  // Prevent "Escape" from closing this modal, and emit the "escape" event
  // instead, so that the parent element can handle it. This way we can be a
  // proper controlled component, instead of having to mutate the `isOpen` prop,
  // or resort to a design that uses `open/close` methods instead of props.
  private onCancel = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    this.escape.emit({});
  };

  // Store the dialog reference
  private setDialog = (dialog?: HTMLDialogElement) => {
    this.dialog = dialog;
  };
}
